import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from 'src/app/authentication/services/auth.service';
import { AccountsService } from '../../services/accounts.service';
import { Account } from '../../models/account.model';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { TranslateService } from '@ngx-translate/core';
import { Language } from '../../models/language.model';
import { AuthData } from 'src/app/authentication/models/auth-data.model';
import { AccountContextService } from 'src/app/authentication/services/account-context.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss']
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  username: string = '';
  accounts: Account[] = [];
  languages: Language[] = [];
  selectedAccount: Account;
  selectedLanguage: Language;
  selectedAccountId: string;
  loading: boolean;
  private destroy$ = new Subject<void>();
  colors = [
    'rgb(255, 215, 101)',
    'rgb(255, 158, 143)',
    'rgb(135, 216, 114)',
    'rgb(114, 216, 207)',
    'rgb(143, 169, 228)',
    'rgb(216, 161, 234)',
    'rgb(234, 161, 201)'
  ];

  headerStyle = {
    background: this.getRandomColor()
  }

  constructor(
    private authService: AuthService,
    private accountContext: AccountContextService,
    private accountsService: AccountsService,
    private translateService: TranslateService) { }

  ngOnInit() {
    const authData = this.authService.getAuthData();

    if (!authData) {
      this.authService.expireSession();
      return;
    }

    this.username = authData.username || '';
    this.selectedAccountId = this.accountContext.getSelectedAccountId();

    this.fillLanguages();
    this.getAccountsInfo(authData);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout() {
    this.authService.logout();
  }

  handleAccountSelectionChange(event: MatSelectChange) {
    const selectedAccountId = event.value?.AccountID;

    if (!selectedAccountId) {
      return;
    }

    this.selectedAccount = this.accounts.filter(a => a.AccountID === selectedAccountId)[0];

    if (!this.selectedAccount) {
      return;
    }

    const accountId = this.selectedAccount.AccountID.toString();

    if (!this.accountContext.setSelectedAccountId(accountId)) {
      this.selectedAccount = this.accounts.filter(a => a.AccountID === this.accountContext.getSelectedAccountId())[0];
    }
  }

  handleLanguageSelectionChange(event) {
    this.translateService.use(event.value.key);
    localStorage.setItem("lang", event.value.key);
    this.setSelectedLanguage();
  }

  private getAccountsInfo(authData: AuthData) {
    const userAccounts = this.accountContext.getAccounts();

    if (userAccounts.length > 0) {
      this.loading = true;
      let defaultAccount = authData.accountID || this.accountContext.getDefaultAccountId();
      if (!userAccounts.includes(this.selectedAccountId))
        this.selectedAccountId = defaultAccount;
      userAccounts.map(a => {
        this.accountsService.getAccount(a)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
          res => {
            this.accounts.push(res);
            if (a === this.selectedAccountId)
              this.selectedAccount = res;
          },
          e => {
            console.log(e);
          },
          () => {
            this.loading = false;
          }
        );
      });
    }
  }

  private fillLanguages() {
    this.languages = [
      {
        key: 'pt',
        name: this.translateService.get('languages.pt')
      },
      {
        key: 'en',
        name: this.translateService.get('languages.en')
      }
      ,
      {
        key: 'es',
        name: this.translateService.get('languages.es')
      }
    ]

    this.setSelectedLanguage();
  }

  private setSelectedLanguage() {
    const storedLanguage = localStorage.getItem("lang");
    const languageKey = this.languages.some(l => l.key === storedLanguage) ? storedLanguage : 'en';

    if (storedLanguage !== languageKey) {
      localStorage.setItem("lang", languageKey);
      this.translateService.use(languageKey);
    }

    this.selectedLanguage = this.languages.filter(l => l.key === languageKey)[0];
    this.selectedLanguage.name = this.translateService.get(`languages.${languageKey}`);
  }

  private getRandomColor() {
    return this.colors[Math.floor(Math.random() * this.colors.length)]
  }
}
